import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import client from '../api/client';
import './RecipeForm.css';

const emptyIngredient = () => ({ name: '', quantity: '', unit: '' });
const emptyStep = () => ({ description: '' });
const emptyImage = () => ({ url: '' });

function RecipeForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id) && id !== 'new';

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [prepTime, setPrepTime] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [ingredients, setIngredients] = useState([emptyIngredient()]);
  const [steps, setSteps] = useState([emptyStep()]);
  const [images, setImages] = useState([emptyImage()]);
  const [rotations, setRotations] = useState({});
  const [uploading, setUploading] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isEdit) return;
    client.get(`/api/recipes/${id}`)
      .then(res => {
        const r = res.data;
        setTitle(r.title || '');
        setDescription(r.description || '');
        setPrepTime(r.prep_time != null ? String(r.prep_time) : '');
        setDifficulty(r.difficulty || '');
        setIngredients(r.ingredients.length > 0 ? r.ingredients : [emptyIngredient()]);
        setSteps(r.steps.length > 0 ? r.steps : [emptyStep()]);
        setImages(r.images.length > 0 ? r.images : [emptyImage()]);
      })
      .catch(() => setError('Error al cargar la receta.'))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const handleImageUpload = async (idx, file) => {
    if (!file) return;
    setUploading(idx);
    setError('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await client.post('/api/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImages(prev => prev.map((img, i) => i === idx ? { url: res.data.url } : img));
    } catch (err) {
      setError(err.response?.data?.error || 'Error al subir la imagen');
    } finally {
      setUploading(null);
    }
  };

  const rotateImage = (idx) => {
    setRotations(prev => ({ ...prev, [idx]: ((prev[idx] || 0) + 90) % 360 }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!title.trim()) { setError('El título es obligatorio.'); return; }

    const payload = {
      title: title.trim(),
      description: description.trim(),
      prep_time: prepTime ? parseInt(prepTime, 10) : null,
      difficulty: difficulty || null,
      ingredients: ingredients.filter(i => i.name.trim()),
      steps: steps
        .filter(s => s.description.trim())
        .map((s, idx) => ({ order_num: idx + 1, description: s.description.trim() })),
      images: images.filter(img => img.url.trim()),
    };

    setSubmitting(true);
    try {
      let res;
      if (isEdit) {
        res = await client.put(`/api/recipes/${id}`, payload);
      } else {
        res = await client.post('/api/recipes', payload);
      }
      navigate(`/recipes/${res.data.id}`);
    } catch (err) {
      const msg = err.response?.data?.error || 'Algo salió mal.';
      setError(msg);
      setSubmitting(false);
    }
  };

  const updateIngredient = (idx, field, val) => {
    setIngredients(prev => prev.map((ing, i) => i === idx ? { ...ing, [field]: val } : ing));
  };
  const addIngredient = () => setIngredients(prev => [...prev, emptyIngredient()]);
  const removeIngredient = (idx) => setIngredients(prev => prev.filter((_, i) => i !== idx));

  const updateStep = (idx, val) => {
    setSteps(prev => prev.map((s, i) => i === idx ? { ...s, description: val } : s));
  };
  const addStep = () => setSteps(prev => [...prev, emptyStep()]);
  const removeStep = (idx) => setSteps(prev => prev.filter((_, i) => i !== idx));

  const addImage = () => setImages(prev => [...prev, emptyImage()]);
  const removeImage = (idx) => {
    setImages(prev => prev.filter((_, i) => i !== idx));
    setRotations(prev => { const r = { ...prev }; delete r[idx]; return r; });
  };

  if (loading) return <div className="loading">Cargando receta...</div>;

  return (
    <div className="recipe-form">
      <h1 className="recipe-form-title">{isEdit ? 'Editar receta' : 'Nueva receta'}</h1>
      {error && <div className="error-msg">{error}</div>}

      <form onSubmit={handleSubmit} className="card">
        <div className="form-group">
          <label>Título *</label>
          <input
            className="form-control"
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="ej. Espaguetis a la carbonara clásicos"
            required
            maxLength={200}
          />
        </div>

        <div className="form-group">
          <label>Descripción</label>
          <textarea
            className="form-control"
            rows={3}
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Describe tu receta..."
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Tiempo de preparación (min)</label>
            <input
              className="form-control"
              type="number"
              min="1"
              value={prepTime}
              onChange={e => setPrepTime(e.target.value)}
              placeholder="ej. 30"
            />
          </div>
          <div className="form-group">
            <label>Dificultad</label>
            <select
              className="form-control"
              value={difficulty}
              onChange={e => setDifficulty(e.target.value)}
            >
              <option value="">Seleccionar...</option>
              <option value="easy">Fácil</option>
              <option value="medium">Media</option>
              <option value="hard">Difícil</option>
            </select>
          </div>
        </div>

        <div className="form-section">
          <h2 className="form-section-title">Ingredientes</h2>
          {ingredients.map((ing, idx) => (
            <div key={idx} className="dynamic-row">
              <input
                className="form-control"
                type="text"
                placeholder="Nombre"
                value={ing.name}
                onChange={e => updateIngredient(idx, 'name', e.target.value)}
              />
              <input
                className="form-control form-control-sm"
                type="text"
                placeholder="Cant."
                value={ing.quantity}
                onChange={e => updateIngredient(idx, 'quantity', e.target.value)}
              />
              <input
                className="form-control form-control-sm"
                type="text"
                placeholder="Unidad"
                value={ing.unit}
                onChange={e => updateIngredient(idx, 'unit', e.target.value)}
              />
              {ingredients.length > 1 && (
                <button
                  type="button"
                  className="btn-remove"
                  onClick={() => removeIngredient(idx)}
                  aria-label="Eliminar ingrediente"
                >✕</button>
              )}
            </div>
          ))}
          <button type="button" className="btn btn-outline btn-add" onClick={addIngredient}>
            + Añadir ingrediente
          </button>
        </div>

        <div className="form-section">
          <h2 className="form-section-title">Pasos</h2>
          {steps.map((step, idx) => (
            <div key={idx} className="step-row">
              <span className="step-badge">{idx + 1}</span>
              <textarea
                className="form-control"
                rows={2}
                placeholder={`Paso ${idx + 1}...`}
                value={step.description}
                onChange={e => updateStep(idx, e.target.value)}
              />
              {steps.length > 1 && (
                <button
                  type="button"
                  className="btn-remove"
                  onClick={() => removeStep(idx)}
                  aria-label="Eliminar paso"
                >✕</button>
              )}
            </div>
          ))}
          <button type="button" className="btn btn-outline btn-add" onClick={addStep}>
            + Añadir paso
          </button>
        </div>

        <div className="form-section">
          <h2 className="form-section-title">Imágenes</h2>
          {images.map((img, idx) => (
            <div key={idx} className="image-row">
              <div className="image-input-group">
                <label className={`btn btn-outline btn-file${uploading === idx ? ' disabled' : ''}`}>
                  {uploading === idx ? 'Subiendo...' : img.url ? 'Cambiar imagen' : 'Subir imagen'}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    onChange={e => {
                      if (e.target.files[0]) handleImageUpload(idx, e.target.files[0]);
                    }}
                    hidden
                    disabled={uploading === idx}
                  />
                </label>
                {img.url && (
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={() => rotateImage(idx)}
                  >
                    Rotar
                  </button>
                )}
                {images.length > 1 && (
                  <button
                    type="button"
                    className="btn-remove"
                    onClick={() => removeImage(idx)}
                    aria-label="Eliminar imagen"
                  >✕</button>
                )}
              </div>
              {img.url && (
                <div className="image-preview">
                  <img
                    src={img.url}
                    alt="Preview"
                    style={{ transform: `rotate(${rotations[idx] || 0}deg)` }}
                  />
                </div>
              )}
            </div>
          ))}
          <button type="button" className="btn btn-outline btn-add" onClick={addImage}>
            + Añadir imagen
          </button>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Crear receta'}
          </button>
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => navigate(isEdit ? `/recipes/${id}` : '/')}
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}

export default RecipeForm;